import {
  hasWalkSheet,
  STORY_WALK_SHEET,
  storyMapInfo,
  storyNpcsOf,
  storyPortals,
  walkSheetPath,
} from '@rdd/content/story'
import {
  stringProperty,
  tileDistance,
  type CollisionGrid,
  type StoryDirection,
  type StoryMap,
  type StoryMapObject,
  type StoryNpc,
} from '@rdd/contracts'
import type { Spritesheet } from 'pixi.js'
import { Application, Assets, Container, Graphics, Rectangle, Sprite, Text, Texture } from 'pixi.js'
import { AMBIENT, isAmbientKind, particleAt, spawnParticles, type AmbientSpec, type Particle } from './ambient'
import { cameraOffset, tileAt, zoomFor } from './camera'
import { directionOfKey } from './keyboard'
import {
  approach,
  AVATAR_HEIGHT_TILES,
  CANOPY_ALPHA,
  hiddenBehind,
  sheetRow,
  walkFrame,
  walkSheetScale,
  type PropBox,
} from './look'
import { NPC_SCALE_PER_32PX, npcFacing, npcInReach, tilesAround, type PlacedNpc } from './npcs'
import { findPath } from './pathfinding'
import type { RemoteView } from './remote-players'
import { Walker } from './walker'

/**
 * O mundo desenhado (modo_historia/03#HM-003, modo_historia_mundos/03#HM-002). docs/21 secoes 3, 5 e 6,
 * ADR 0042 e 0062.
 *
 * O Pixi so desenha: posicao, colisao e caminho sao de `Walker` e `findPath`, e o quadro da caminhada e a copa
 * translucida sao de `look`, todos puros e testados. Esta classe liga os dois -- carrega as texturas, monta as
 * camadas, segue o personagem com a camera e traduz teclado e toque em passos.
 *
 * **As camadas.** O chao e uma textura so: a imagem do mapa importado, ou as camadas de tiles do `plaza-01`
 * viradas em uma (`cacheAsTexture`). Por cima, UMA camada ordenada pela linha do chao (`zIndex`), com os props
 * e os avatares juntos: e assim que o personagem passa atras da arvore e na frente do banco.
 *
 * **Pixel art em `nearest`**: o chao e os props nao reamostram. A folha de caminhada e reduzida para caber em
 * `AVATAR_HEIGHT_TILES`, e por isso usa `linear`.
 */

export type SceneOptions = {
  mapId: string
  map: StoryMap
  grid: CollisionGrid
  spawn: { x: number; y: number }
  nickname: string
  /** O personagem que o proprio avatar veste (docs/21 secao 6), ou `null` para o sprite padrao. */
  character: string | null
  /** O `userId` do proprio jogador: e assim que um emote dele acha o avatar certo. */
  selfId?: string
  /** A base dos arquivos em `public/` (`import.meta.env.BASE_URL`). */
  assetsBase: string
  reducedMotion: boolean
  /** Cada passo que comeca -- e o que o servidor recebe em modo_historia/04. */
  onStep?: (dir: StoryDirection) => void
  /**
   * Tocou num OUTRO jogador (modo_historia/05): em vez de andar ate o tile dele, a tela oferece o desafio. A
   * distancia vai junto, em tiles, para a tela decidir se o desafio esta ao alcance.
   */
  onSelectPlayer?: (id: string, nick: string, distance: number) => void
  /** O NPC ao alcance da conversa mudou (docs/21 secao 12): a tela mostra ou esconde o "falar". */
  onNearNpc?: (npc: StoryNpc | null) => void
  /** O jogador pediu para falar com o NPC ao alcance: tecla ou toque nele. */
  onTalk?: (npc: StoryNpc) => void
}

/** Quanto tempo o personagem parado pode discordar do servidor antes de voltar ao tile dele. */
const CORRECTION_MS = 600

/** As camadas de tiles desenhadas, nesta ordem. `collision` e `spawn` sao dados, e nao aparecem. */
const DRAWN_LAYERS = ['ground', 'decor']

/** As teclas de falar com o NPC: o "E" do IDLE FORCE, e o Enter e o espaco de quem nao o conhece. */
const TALK_KEYS = ['KeyE', 'Enter', 'Space']

const isInteractive = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement && ['BUTTON', 'INPUT', 'TEXTAREA', 'SELECT', 'A'].includes(target.tagName)

/** O lado da textura do ponto suave do clima, em pixels. */
const SOFT_DOT_PIXELS = 32

let softDotTexture: Texture | null = null

/** Um ponto branco com borda que some: o gradiente radial que o IDLE FORCE usa para luz e particula. */
function softDot(): Texture {
  if (softDotTexture !== null) return softDotTexture
  const canvas = document.createElement('canvas')
  canvas.width = SOFT_DOT_PIXELS
  canvas.height = SOFT_DOT_PIXELS
  const context = canvas.getContext('2d')
  if (context !== null) {
    const half = SOFT_DOT_PIXELS / 2
    const gradient = context.createRadialGradient(half, half, 0, half, half, half)
    gradient.addColorStop(0, 'rgba(255,255,255,1)')
    gradient.addColorStop(0.4, 'rgba(255,255,255,0.55)')
    gradient.addColorStop(1, 'rgba(255,255,255,0)')
    context.fillStyle = gradient
    context.fillRect(0, 0, SOFT_DOT_PIXELS, SOFT_DOT_PIXELS)
  }
  softDotTexture = Texture.from(canvas)
  return softDotTexture
}

/** A cor do brilho do portal. */
const PORTAL_COLOR = 0xffd27a

/** O sprite padrao (docs/21 secao 6) e um quadro de 16 px do pack Kenney. */
const DEFAULT_SPRITE_PIXELS = 16

/** As texturas de um personagem: uma linha por direcao, um quadro por passo. */
type WalkFrames = readonly (readonly Texture[])[]

/** O que a cena guarda de cada avatar para animar: o corpo, a altura e quanto ele ja andou. */
type AvatarParts = {
  character: string | null
  body: Sprite
  height: number
  walked: number
  last: { x: number; y: number } | null
}

type Prop = { sprite: Sprite; box: PropBox }

export class WorldScene {
  readonly app = new Application()
  readonly walker: Walker
  private readonly world = new Container()
  private readonly depthLayer = new Container({ sortableChildren: true })
  private readonly avatar = new Container()
  private readonly remoteAvatars = new Map<string, Container>()
  private readonly parts = new WeakMap<Container, AvatarParts>()
  private readonly props: Prop[] = []
  private readonly portalGlows: Graphics[] = []
  private readonly portalLabels: Text[] = []
  private elapsedMs = 0
  private readonly npcViews: Array<{ placed: PlacedNpc; sprite: Sprite; frames: Texture[] }> = []
  private nearNpc: PlacedNpc | undefined
  private readonly ambientLayer = new Container()
  private ambient: { spec: AmbientSpec; particles: Array<{ particle: Particle; sprite: Sprite }> } | null = null
  private defaultTexture: Texture | null = null
  private readonly sheets = new Map<string, WalkFrames | 'loading'>()
  private serverSelf: { x: number; y: number } | null = null
  private divergedMs = 0
  /** Fonte dos outros jogadores, lida a cada quadro (modo_historia/04). */
  remotes: (() => RemoteView[]) | null = null
  /** Avanca a interpolacao dos outros jogadores. */
  advanceRemotes: ((deltaMs: number) => void) | null = null
  private zoom = 3
  private readonly cleanup: Array<() => void> = []
  private host: HTMLElement | null = null

  constructor(private readonly options: SceneOptions) {
    this.walker = new Walker(options.grid, options.spawn.x, options.spawn.y)
  }

  async mount(host: HTMLElement): Promise<void> {
    this.host = host
    await this.app.init({ resizeTo: host, background: '#1b1b24', antialias: false, autoDensity: true, resolution: 1 })
    host.append(this.app.canvas)
    this.app.canvas.setAttribute('aria-hidden', 'true')

    await this.buildGround()
    this.buildPortals()
    await this.buildProps()
    await this.buildNpcs()
    const defaultTexture = await Assets.load<Texture>(`${this.options.assetsBase}story/avatar.webp`)
    defaultTexture.source.scaleMode = 'nearest'
    this.defaultTexture = defaultTexture
    if (hasWalkSheet(this.options.character)) await this.loadSheet(this.options.character ?? '')
    this.dressAvatar(this.avatar, this.options.nickname, this.options.character)
    this.depthLayer.addChild(this.avatar)
    this.world.addChild(this.depthLayer)
    this.buildAmbient()
    this.world.addChild(this.ambientLayer)
    this.app.stage.addChild(this.world)

    this.relayout()
    this.app.ticker.add((ticker) => this.tick(ticker.deltaMS))
    this.bindInput()
    // So agora a cena esta pronta: o canvas aparece antes, enquanto as texturas carregam, e uma tecla
    // apertada nessa janela se perderia. O `data-tile` e o sinal de "pronto" -- e o que o e2e espera.
    host.dataset['tile'] = `${this.walker.tileX},${this.walker.tileY}`
  }

  destroy(): void {
    for (const off of this.cleanup) off()
    this.app.destroy(true, { children: true })
  }

  private get selfId(): string | undefined {
    return this.options.selfId
  }

  private get tilePixels(): number {
    return this.options.map.tilewidth
  }

  private mapAsset(file: string): string {
    return `${this.options.assetsBase}story/maps/${this.options.mapId}/${file}`
  }

  /** O chao: a imagem do mapa importado, ou as camadas de tiles viradas em uma textura. */
  private async buildGround(): Promise<void> {
    const { map } = this.options
    const ground = map.layers.find((layer) => layer.name === 'ground')
    if (ground?.type === 'imagelayer') {
      const texture = await Assets.load<Texture>(this.mapAsset('ground.webp'))
      texture.source.scaleMode = 'nearest'
      this.world.addChild(new Sprite(texture))
      return
    }

    const tileset = map.tilesets[0]
    if (tileset === undefined) return
    const base = await Assets.load<Texture>(`${this.options.assetsBase}story/tilesets/${tileset.name}.webp`)
    base.source.scaleMode = 'nearest'

    const frames = new Map<number, Texture>()
    const frameOf = (gid: number): Texture => {
      const cached = frames.get(gid)
      if (cached !== undefined) return cached
      const index = gid - tileset.firstgid
      const frame = new Rectangle(
        (index % tileset.columns) * tileset.tilewidth,
        Math.floor(index / tileset.columns) * tileset.tileheight,
        tileset.tilewidth,
        tileset.tileheight,
      )
      const texture = new Texture({ source: base.source, frame })
      frames.set(gid, texture)
      return texture
    }

    const tiles = new Container()
    for (const layer of map.layers) {
      if (layer.type !== 'tilelayer' || !DRAWN_LAYERS.includes(layer.name)) continue
      layer.data.forEach((gid, i) => {
        if (gid === 0) return
        const sprite = new Sprite(frameOf(gid))
        sprite.x = (i % map.width) * map.tilewidth
        sprite.y = Math.floor(i / map.width) * map.tileheight
        tiles.addChild(sprite)
      })
    }
    tiles.cacheAsTexture(true)
    this.world.addChild(tiles)
  }

  /**
   * Os portais (docs/21 secao 5): um brilho no chao e o nome do destino por cima, para o jogador saber onde pisar
   * e para onde vai. O nome e o do mapa de destino, que e conteudo (`label`).
   */
  private buildPortals(): void {
    const px = this.tilePixels
    for (const portal of storyPortals(this.options.mapId)) {
      const glow = new Graphics()
        .ellipse(0, 0, px * 0.46, px * 0.22)
        .fill({ color: PORTAL_COLOR, alpha: 0.35 })
        .ellipse(0, 0, px * 0.46, px * 0.22)
        .stroke({ color: PORTAL_COLOR, width: 2, alpha: 0.9 })
      glow.position.set((portal.x + 0.5) * px, (portal.y + 0.6) * px)
      this.world.addChild(glow)
      this.portalGlows.push(glow)

      const label = new Text({
        text: storyMapInfo(portal.to)?.label ?? portal.to,
        style: {
          fontFamily: 'system-ui, sans-serif',
          fontSize: 12,
          fill: '#fff4c8',
          stroke: { color: '#1b1b24', width: 3 },
        },
        resolution: 2,
      })
      label.anchor.set(0.5, 1)
      label.position.set((portal.x + 0.5) * px, portal.y * px)
      label.zIndex = Number.MAX_SAFE_INTEGER
      this.depthLayer.addChild(label)
      this.portalLabels.push(label)
    }
  }

  /** Os props do mapa (docs/21 secao 3), cada um na linha do chao dele, no atlas do mapa. */
  private async buildProps(): Promise<void> {
    const layer = this.options.map.layers.find((candidate) => candidate.name === 'props')
    if (layer?.type !== 'objectgroup' || layer.objects.length === 0) return
    const sheet = await Assets.load<Spritesheet>(this.mapAsset('props.json'))
    sheet.textureSource.scaleMode = 'nearest'
    for (const object of layer.objects) {
      const texture = sheet.textures[stringProperty(object, 'sprite') ?? '']
      if (texture === undefined) continue
      const sprite = new Sprite(texture)
      sprite.position.set(object.x, object.y)
      const box = propBox(object, texture)
      sprite.zIndex = box.depth
      this.depthLayer.addChild(sprite)
      this.props.push({ sprite, box })
    }
  }

  /**
   * Os NPCs do mapa (docs/21 secao 12): parados no tile deles, na camada ordenada, com o nome por cima. A folha
   * tem as quatro direcoes lado a lado, na ordem da folha de caminhada.
   */
  private async buildNpcs(): Promise<void> {
    const px = this.tilePixels
    for (const placed of storyNpcsOf(this.options.mapId)) {
      const base = await Assets.load<Texture>(`${this.options.assetsBase}story/npcs/${placed.npc.id}/sprite.webp`)
      base.source.scaleMode = 'nearest'
      const width = base.width / STORY_WALK_SHEET.rows.length
      const frames = STORY_WALK_SHEET.rows.map(
        (_, i) => new Texture({ source: base.source, frame: new Rectangle(i * width, 0, width, base.height) }),
      )
      const holder = new Container()
      const shadow = new Graphics().ellipse(0, 0, px * 0.38, px * 0.14).fill({ color: 0x000000, alpha: 0.28 })
      const sprite = new Sprite(frames[sheetRow(placed.npc.facing)] ?? frames[0])
      sprite.anchor.set(0.5, 1)
      sprite.scale.set((NPC_SCALE_PER_32PX * px) / 32)
      const name = new Text({
        text: placed.npc.name,
        style: {
          fontFamily: 'system-ui, sans-serif',
          fontSize: 14,
          fill: '#ffe39a',
          stroke: { color: '#1b1b24', width: 3 },
        },
        resolution: 2,
      })
      name.label = 'name'
      name.anchor.set(0.5, 1)
      name.scale.set(1 / this.zoom)
      name.y = -sprite.height - 2
      holder.addChild(shadow, sprite, name)
      holder.position.set((placed.x + 0.5) * px, (placed.y + 1) * px)
      holder.zIndex = holder.y
      this.depthLayer.addChild(holder)
      this.npcViews.push({ placed, sprite, frames })
    }
  }

  /**
   * O clima do mapa (modo_historia_mundos/07): particulas da semente do mapa por cima de tudo, com a luz aditiva
   * do IDLE FORCE nas que brilham. Sem movimento (`prefers-reduced-motion`), nao ha clima: particula parada e
   * sujeira na tela.
   */
  private buildAmbient(): void {
    const kind = stringProperty(this.options.map, 'ambient')
    if (this.options.reducedMotion || !isAmbientKind(kind)) return
    const { map } = this.options
    const spec = AMBIENT[kind]
    const texture = softDot()
    const px = this.tilePixels
    this.ambient = {
      spec,
      particles: spawnParticles(this.options.mapId, kind, map.width, map.height).map((particle) => {
        const sprite = new Sprite(texture)
        sprite.anchor.set(0.5)
        sprite.tint = spec.color
        sprite.blendMode = spec.glow ? 'add' : 'normal'
        // A nuvem e uma sombra larga e fraca no chao; o resto e um ponto.
        sprite.scale.set((spec.size * px * 2) / SOFT_DOT_PIXELS)
        if (kind === 'clouds') sprite.scale.y *= 0.55
        this.ambientLayer.addChild(sprite)
        return { particle, sprite }
      }),
    }
    if (kind === 'clouds') this.ambientLayer.alpha = 0.16
  }

  private updateAmbient(): void {
    if (this.ambient === null) return
    const { map } = this.options
    const px = this.tilePixels
    const seconds = this.elapsedMs / 1000
    for (const { particle, sprite } of this.ambient.particles) {
      const at = particleAt(particle, this.ambient.spec, seconds, map.width, map.height)
      sprite.position.set(at.x * px, at.y * px)
      sprite.alpha = at.alpha
    }
  }

  /** O NPC olha para o jogador que chega perto, e o "falar" aparece ao alcance (docs/21 secao 12). */
  private updateNpcs(): void {
    const self = { x: this.walker.tileX, y: this.walker.tileY }
    for (const view of this.npcViews) {
      const texture = view.frames[sheetRow(npcFacing(view.placed, self))]
      if (texture !== undefined && view.sprite.texture !== texture) view.sprite.texture = texture
    }
    const near = npcInReach(
      this.npcViews.map((view) => view.placed),
      self,
    )
    if (near?.npc.id === this.nearNpc?.npc.id) return
    this.nearNpc = near
    this.options.onNearNpc?.(near?.npc ?? null)
    // O NPC ao alcance, no DOM: e o que o e2e le.
    if (this.host !== null) this.host.dataset['npc'] = near?.npc.id ?? ''
  }

  /** Carrega a folha de caminhada de um personagem, uma vez. Quem pede antes de chegar anda com o padrao. */
  private async loadSheet(characterId: string): Promise<void> {
    if (this.sheets.has(characterId)) return
    this.sheets.set(characterId, 'loading')
    const base = await Assets.load<Texture>(`${this.options.assetsBase}${walkSheetPath(characterId)}`)
    const { cell, frames, rows } = STORY_WALK_SHEET
    this.sheets.set(
      characterId,
      rows.map((_, row) =>
        Array.from(
          { length: frames },
          (_, frame) =>
            new Texture({ source: base.source, frame: new Rectangle(frame * cell, row * cell, cell, cell) }),
        ),
      ),
    )
  }

  private framesOf(character: string | null): WalkFrames | null {
    if (character === null || !hasWalkSheet(character)) return null
    const sheet = this.sheets.get(character)
    if (sheet === undefined) void this.loadSheet(character)
    return sheet === undefined || sheet === 'loading' ? null : sheet
  }

  /**
   * Um avatar: o personagem (ou o sprite padrao), uma sombra no chao e o nome por cima (docs/21 secao 6).
   * Reconstroi o corpo quando a folha do personagem termina de carregar.
   */
  private dressAvatar(target: Container, nickname: string, character: string | null): void {
    const px = this.tilePixels
    target.getChildByLabel('body')?.destroy()
    const frames = this.framesOf(character)
    const firstFrame = frames?.[0]?.[0]
    let body: Sprite
    let height: number
    if (firstFrame !== undefined) {
      body = new Sprite(firstFrame)
      body.anchor.set(0.5, STORY_WALK_SHEET.baseline / STORY_WALK_SHEET.cell)
      body.scale.set(walkSheetScale(px))
      height = AVATAR_HEIGHT_TILES * px
    } else {
      body = new Sprite(this.defaultTexture ?? Texture.EMPTY)
      body.anchor.set(0.5, 1)
      body.scale.set(px / DEFAULT_SPRITE_PIXELS)
      height = px
    }
    body.label = 'body'

    if (target.getChildByLabel('shadow') === null) {
      const shadow = new Graphics().ellipse(0, 0, px * 0.38, px * 0.14).fill({ color: 0x000000, alpha: 0.28 })
      shadow.label = 'shadow'
      target.addChildAt(shadow, 0)
    }
    target.addChildAt(body, 1)

    let name = target.getChildByLabel('name')
    if (name === null) {
      // O nome por cima, em resolucao propria: texto ampliado com o zoom do mundo ficaria borrado.
      const created = new Text({
        text: nickname,
        style: {
          fontFamily: 'system-ui, sans-serif',
          fontSize: 14,
          fill: '#f4efe6',
          stroke: { color: '#1b1b24', width: 3 },
        },
        resolution: 2,
      })
      created.label = 'name'
      created.anchor.set(0.5, 1)
      created.scale.set(1 / this.zoom)
      target.addChild(created)
      name = created
    }
    name.y = -height - 2

    const previous = this.parts.get(target)
    this.parts.set(target, { character, body, height, walked: previous?.walked ?? 0, last: previous?.last ?? null })
  }

  /** Posiciona um avatar a partir da vista dele (proprio ou de outro) e escolhe o quadro da caminhada. */
  private placeAvatar(target: Container, view: { x: number; y: number; hop: number; facing: StoryDirection }): void {
    const px = this.tilePixels
    const parts = this.parts.get(target)
    if (parts === undefined) return
    // A folha chegou depois do avatar: troca o corpo padrao pelo personagem.
    if (parts.body.texture === this.defaultTexture && this.framesOf(parts.character) !== null) {
      const name = target.getChildByLabel('name')
      this.dressAvatar(target, name instanceof Text ? name.text : '', parts.character)
      this.placeAvatar(target, view)
      return
    }

    const x = (view.x + 0.5) * px
    const y = (view.y + 1) * px
    const moved = parts.last === null ? 0 : Math.hypot(x - parts.last.x, y - parts.last.y)
    parts.walked += moved
    parts.last = { x, y }
    target.position.set(x, y)
    // A linha do chao e o pe: quem esta mais abaixo passa na frente.
    target.zIndex = y

    const frames = this.framesOf(parts.character)
    if (frames !== null) {
      const frame = walkFrame(parts.walked, px, moved > 0)
      const texture = frames[sheetRow(view.facing)]?.[frame]
      if (texture !== undefined) parts.body.texture = texture
      return
    }
    // O sprite padrao tem um quadro so: o passo e um salto leve, e a direcao horizontal e espelhamento.
    parts.body.y = this.options.reducedMotion ? 0 : -view.hop * 2
    if (view.facing === 'left' || view.facing === 'right')
      parts.body.scale.x = (view.facing === 'left' ? -1 : 1) * (px / DEFAULT_SPRITE_PIXELS)
  }

  private drawRemotes(deltaMs: number): void {
    if (this.remotes === null) return
    this.advanceRemotes?.(deltaMs)
    const seen = new Set<string>()
    for (const view of this.remotes()) {
      seen.add(view.id)
      let avatar = this.remoteAvatars.get(view.id)
      if (avatar === undefined || this.parts.get(avatar)?.character !== view.character) {
        avatar?.destroy({ children: true })
        avatar = new Container()
        this.dressAvatar(avatar, view.nick, view.character)
        this.depthLayer.addChild(avatar)
        this.remoteAvatars.set(view.id, avatar)
      }
      this.placeAvatar(avatar, view)
      avatar.alpha = view.dueling ? 0.5 : 1
    }
    for (const [id, avatar] of this.remoteAvatars) {
      if (seen.has(id)) continue
      avatar.destroy({ children: true })
      this.remoteAvatars.delete(id)
    }
    // Quantos outros estao na tela, no DOM: e o que o e2e le, sem ler pixel do canvas.
    if (this.host !== null) this.host.dataset['players'] = String(this.remoteAvatars.size)
  }

  /** A copa fica translucida com o proprio personagem atras dela, como no IDLE FORCE. */
  private fadeCanopies(): void {
    const feet = { x: this.avatar.x, y: this.avatar.y }
    for (const prop of this.props) {
      const target = hiddenBehind(feet, prop.box, this.tilePixels) ? CANOPY_ALPHA : 1
      if (prop.sprite.alpha !== target) prop.sprite.alpha = approach(prop.sprite.alpha, target)
    }
  }

  /**
   * Um emote sobre o avatar de quem mandou, por `STORY_EMOTE_INTERVAL_MS` (docs/21 secao 7). O balao segue o
   * avatar porque e filho dele.
   */
  showEmote(playerId: string, glyph: string, durationMs: number): void {
    const avatar = playerId === this.selfId ? this.avatar : this.remoteAvatars.get(playerId)
    if (avatar === undefined) return
    avatar.getChildByLabel('emote')?.destroy()
    // O ultimo emote visto, no DOM, pelo mesmo motivo do `data-players`.
    if (this.host !== null) this.host.dataset['emote'] = `${playerId}:${glyph}`
    const bubble = new Text({ text: glyph, style: { fontSize: 22 }, resolution: 2 })
    bubble.anchor.set(0.5, 1)
    bubble.label = 'emote'
    bubble.scale.set(1 / this.zoom)
    bubble.y = -(this.parts.get(avatar)?.height ?? this.tilePixels) - 16 / this.zoom
    avatar.addChild(bubble)
    setTimeout(() => {
      if (!bubble.destroyed) bubble.destroy()
    }, durationMs)
  }

  /**
   * A posicao que o servidor confirmou para o PROPRIO personagem (docs/21 secao 5). O cliente previu o passo;
   * se, parado, ele continua discordando do servidor por `CORRECTION_MS`, volta ao tile do servidor. O prazo
   * cobre a ida e volta do passo previsto: sem ele, todo passo seria desfeito antes de o servidor confirma-lo.
   * Um passo que o servidor recusou nunca vem no `delta` -- nada mudou la --, e e por isso que a conferencia
   * corre a cada quadro, e nao so quando chega mensagem.
   */
  authoritative(x: number, y: number): void {
    this.serverSelf = { x, y }
  }

  private correct(deltaMs: number): void {
    const server = this.serverSelf
    if (server === null || this.walker.moving || (this.walker.tileX === server.x && this.walker.tileY === server.y)) {
      this.divergedMs = 0
      return
    }
    this.divergedMs += deltaMs
    if (this.divergedMs < CORRECTION_MS) return
    this.walker.place(server.x, server.y)
    this.divergedMs = 0
  }

  private relayout(): void {
    this.zoom = zoomFor({ width: this.app.screen.width, height: this.app.screen.height }, this.tilePixels)
    this.world.scale.set(this.zoom)
    // O texto nao escala com o mundo: desfaz o zoom nele, no proprio e nos outros.
    for (const avatar of [this.avatar, ...this.remoteAvatars.values()])
      avatar.getChildByLabel('name')?.scale.set(1 / this.zoom)
    for (const label of this.portalLabels) label.scale.set(1 / this.zoom)
    for (const view of this.npcViews) view.sprite.parent?.getChildByLabel('name')?.scale.set(1 / this.zoom)
  }

  private tick(deltaMs: number): void {
    const started = this.walker.update(deltaMs)
    for (const dir of started) this.options.onStep?.(dir)
    // O tile atual no DOM: e por ele que o e2e confere que o personagem andou, sem ler pixel do canvas.
    if (started.length > 0 && this.host !== null)
      this.host.dataset['tile'] = `${this.walker.tileX},${this.walker.tileY}`
    this.correct(deltaMs)
    const view = this.walker.view()
    const px = this.tilePixels
    this.placeAvatar(this.avatar, view)
    this.drawRemotes(deltaMs)
    this.fadeCanopies()
    this.updateNpcs()
    this.updateAmbient()
    this.elapsedMs += deltaMs
    // O brilho do portal pulsa devagar; sem movimento, fica parado.
    if (!this.options.reducedMotion)
      for (const glow of this.portalGlows) glow.alpha = 0.7 + 0.3 * Math.sin(this.elapsedMs / 400)

    const { map } = this.options
    const offset = cameraOffset(
      { x: this.avatar.x * this.zoom, y: (this.avatar.y - px / 2) * this.zoom },
      { width: map.width * px * this.zoom, height: map.height * px * this.zoom },
      { width: this.app.screen.width, height: this.app.screen.height },
    )
    if (this.options.reducedMotion) {
      this.world.position.set(offset.x, offset.y)
    } else {
      // Suavizacao curta: a camera alcanca o personagem sem dar tranco a cada passo.
      this.world.x += (offset.x - this.world.x) * 0.25
      this.world.y += (offset.y - this.world.y) * 0.25
    }
    // A camera no DOM (deslocamento e zoom): e com ela que o e2e acha o pixel de um tile para tocar num jogador.
    const camera = `${Math.round(this.world.x)},${Math.round(this.world.y)},${this.zoom}`
    if (this.host !== null && this.host.dataset['camera'] !== camera) this.host.dataset['camera'] = camera
  }

  private bindInput(): void {
    const held = new Set<StoryDirection>()
    const onKeyDown = (event: KeyboardEvent): void => {
      // Falar com o NPC ao alcance (docs/21 secao 12). So fora de um campo ou botao, que tem o Enter dele.
      if (TALK_KEYS.includes(event.code) && this.nearNpc !== undefined && !isInteractive(event.target)) {
        event.preventDefault()
        this.options.onTalk?.(this.nearNpc.npc)
        return
      }
      const dir = directionOfKey(event.code)
      if (dir === undefined) return
      event.preventDefault()
      held.add(dir)
      if (!this.walker.moving) this.walker.walk(dir)
    }
    const onKeyUp = (event: KeyboardEvent): void => {
      const dir = directionOfKey(event.code)
      if (dir !== undefined) held.delete(dir)
    }
    // Segurar a tecla anda continuamente: a cada passo concluido, o proximo sai da tecla ainda pressionada.
    this.app.ticker.add(() => {
      if (this.walker.moving) return
      const last = [...held].pop()
      if (last !== undefined) this.walker.walk(last)
    })

    const onPointer = (event: PointerEvent): void => {
      const rect = this.app.canvas.getBoundingClientRect()
      const tile = tileAt(
        { x: event.clientX - rect.left, y: event.clientY - rect.top },
        { x: this.world.x, y: this.world.y },
        this.tilePixels * this.zoom,
      )
      const tapped = this.remotes?.().find((view) => Math.round(view.x) === tile.x && Math.round(view.y) === tile.y)
      if (tapped !== undefined && this.options.onSelectPlayer !== undefined) {
        this.options.onSelectPlayer(
          tapped.id,
          tapped.nick,
          tileDistance({ x: this.walker.tileX, y: this.walker.tileY }, tile),
        )
        return
      }
      const self = { x: this.walker.tileX, y: this.walker.tileY }
      const npc = this.npcViews.find((view) => view.placed.x === tile.x && view.placed.y === tile.y)?.placed
      if (npc !== undefined) {
        // Tocou num NPC: ao alcance, fala; longe, anda ate o tile livre mais perto dele.
        if (npcInReach([npc], self) !== undefined) {
          this.options.onTalk?.(npc.npc)
          return
        }
        const paths = tilesAround(npc)
          .map((target) => findPath(this.options.grid, self, target))
          .filter((candidate) => candidate !== null)
        const shortest = paths.sort((a, b) => a.length - b.length)[0]
        if (shortest !== undefined) this.walker.follow(shortest)
        return
      }
      const path = findPath(this.options.grid, self, tile)
      if (path !== null) this.walker.follow(path)
    }
    const onResize = (): void => this.relayout()

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    this.app.canvas.addEventListener('pointerdown', onPointer)
    window.addEventListener('resize', onResize)
    this.cleanup.push(
      () => window.removeEventListener('keydown', onKeyDown),
      () => window.removeEventListener('keyup', onKeyUp),
      () => window.removeEventListener('resize', onResize),
    )
  }
}

/** O retangulo de um prop, com a linha do chao da propriedade `depth`, ou a base do desenho sem ela. */
function propBox(object: StoryMapObject, texture: Texture): PropBox {
  const width = object.width ?? texture.width
  const height = object.height ?? texture.height
  const depth = object.properties?.find((property) => property.name === 'depth')?.value
  return { x: object.x, y: object.y, width, height, depth: typeof depth === 'number' ? depth : object.y + height }
}
